import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { API_BASE } from '../lib/config';
import { useAuthStore } from '../stores/authStore';

type AuthStep = 'initial' | 'sending' | 'verification' | 'code' | 'verifying';

export function SignInPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const token = useAuthStore((s) => s.token);
  const [email, setEmail] = useState('');
  const [authStep, setAuthStep] = useState<AuthStep>('initial');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Already logged in — skip sign-in page entirely
  if (token) {
    return <Navigate to="/chat" replace />;
  }

  const handleGoogleSignIn = async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/google/url`);
      if (!res.ok) {
        setError('Failed to connect to Google. Please try again.');
        return;
      }
      const data = await res.json();
      if (typeof data.url !== 'string' || !data.url.startsWith('https://')) {
        setError('Invalid response from server. Please try again.');
        return;
      }
      window.location.href = data.url;
    } catch {
      setError('Failed to connect to Google. Please try again.');
    }
  };

  const handleZoomSignIn = async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/zoom/url`);
      if (!res.ok) {
        setError('Failed to connect to Zoom. Please try again.');
        return;
      }
      const data = await res.json();
      if (typeof data.url !== 'string' || !data.url.startsWith('https://')) {
        setError('Invalid response from server. Please try again.');
        return;
      }
      window.location.href = data.url;
    } catch {
      setError('Failed to connect to Zoom. Please try again.');
    }
  };

  const handleEmailSignIn = async () => {
    if (!email.trim()) return;
    setError(null);
    setAuthStep('sending');

    try {
      const res = await fetch(`${API_BASE}/auth/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || 'Failed to send verification code.');
        setAuthStep('initial');
        return;
      }

      setAuthStep('verification');
    } catch {
      setError('Network error. Please try again.');
      setAuthStep('initial');
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) return;
    setError(null);
    setAuthStep('verifying');

    try {
      const res = await fetch(`${API_BASE}/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: verificationCode.trim() }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || 'Invalid or expired code.');
        setAuthStep('code');
        return;
      }

      const data = await res.json();
      setAuth({
        token: data.token,
        user: data.user,
      });
      navigate('/chat', { replace: true });
    } catch {
      setError('Network error. Please try again.');
      setAuthStep('code');
    }
  };

  const handleResendEmail = async () => {
    setError(null);
    try {
      await fetch(`${API_BASE}/auth/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch {
      // silently fail
    }
  };

  return (
    <main className="flex items-center justify-center min-h-[calc(100vh-88px)] px-8">
        <div className="flex items-center gap-16 max-w-6xl w-full">
          {/* Left side - Copy and CTA */}
          <div className="flex-1 max-w-md">
            <h1 className="text-5xl font-serif italic mb-2">
              Discover.
            </h1>
            <h1 className="text-5xl font-serif italic text-primary mb-6">
              Adopt.
            </h1>
            <p className="text-muted-foreground text-lg mb-10">
              The on-demand adoption expert for Zoom admins
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            {(authStep === 'initial' || authStep === 'sending') && (
              <>
                <div className="space-y-4">
                  <button
                    onClick={handleGoogleSignIn}
                    className="w-full bg-white hover:bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-3 border border-gray-300"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>

                  <button
                    onClick={handleZoomSignIn}
                    className="w-full bg-[#2D8CFF] hover:bg-[#2681F0] text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-3"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M4.585 16.56V9.89c0-1.127.913-2.04 2.04-2.04h7.748l-3.18 2.727H6.625v4.943l-2.04 1.04zm14.83-9.12v6.67c0 1.127-.913 2.04-2.04 2.04H9.627l3.18-2.727h4.568V8.48l2.04-1.04z"/>
                    </svg>
                    Continue with Zoom
                  </button>

                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-muted-foreground text-sm">OR</span>
                    <div className="flex-1 h-px bg-border"></div>
                  </div>

                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleEmailSignIn()}
                    placeholder="Enter your email"
                    className="w-full bg-input border border-border rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />

                  <button
                    onClick={handleEmailSignIn}
                    disabled={authStep === 'sending'}
                    className="w-full bg-secondary hover:bg-secondary/80 text-foreground px-6 py-3 rounded-lg font-medium transition-colors border border-border disabled:opacity-50"
                  >
                    {authStep === 'sending' ? 'Sending...' : 'Continue with email'}
                  </button>
                </div>

                <p className="text-muted-foreground text-xs mt-6 text-center">
                  By continuing, you acknowledge our{' '}
                  <a href="#" className="text-primary hover:underline">Privacy Policy</a>
                  {' '}and{' '}
                  <a href="#" className="text-primary hover:underline">Terms of Service</a>.
                </p>
              </>
            )}

            {authStep === 'verification' && (
              <div className="bg-card border border-border rounded-xl p-8 text-center">
                <Mail className="w-8 h-8 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground text-sm mb-1">To continue, enter the code sent to</p>
                <p className="font-medium mb-6">{email}</p>

                <div className="space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    <button
                      onClick={() => setAuthStep('code')}
                      className="text-primary hover:underline"
                    >
                      Enter verification code
                    </button>
                  </p>
                  <p className="text-muted-foreground">
                    Not seeing the email in your inbox?{' '}
                    <button
                      onClick={handleResendEmail}
                      className="text-primary hover:underline"
                    >
                      Try sending again
                    </button>
                  </p>
                </div>
              </div>
            )}

            {(authStep === 'code' || authStep === 'verifying') && (
              <div className="bg-card border border-border rounded-xl p-8 text-center">
                <p className="text-muted-foreground text-sm mb-1">Enter the verification code sent to</p>
                <p className="font-medium mb-6">{email}</p>

                <div className="space-y-4">
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="w-full bg-input border border-border rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-center tracking-widest text-lg"
                  />

                  <button
                    onClick={handleVerifyCode}
                    disabled={authStep === 'verifying'}
                    className="w-full bg-white hover:bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium transition-colors border border-gray-300 disabled:opacity-50"
                  >
                    {authStep === 'verifying' ? 'Verifying...' : 'Verify Code'}
                  </button>
                </div>

                <p className="text-muted-foreground text-sm mt-6">
                  Not seeing the email in your inbox?{' '}
                  <button
                    onClick={handleResendEmail}
                    className="text-primary hover:underline"
                  >
                    Try sending again
                  </button>
                </p>
              </div>
            )}
          </div>

          {/* Right side - Visual */}
          <div className="flex-1 hidden lg:block">
            <div className="relative">
              {/* Abstract gradient background */}
              <div className="w-full aspect-[4/5] rounded-2xl bg-gradient-to-br from-primary/30 via-purple-500/20 to-blue-500/30 overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-3/4 h-3/4">
                    {/* Floating cards representing Zoom products */}
                    <div className="absolute top-0 left-0 bg-card border border-border rounded-xl p-4 shadow-xl transform -rotate-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                          <span className="text-blue-400 text-lg">📞</span>
                        </div>
                        <div>
                          <div className="text-sm font-medium">Zoom Phone</div>
                          <div className="text-xs text-muted-foreground">12 features available</div>
                        </div>
                      </div>
                    </div>

                    <div className="absolute top-1/4 right-0 bg-card border border-border rounded-xl p-4 shadow-xl transform rotate-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                          <span className="text-green-400 text-lg">🤖</span>
                        </div>
                        <div>
                          <div className="text-sm font-medium">AI Companion</div>
                          <div className="text-xs text-muted-foreground">88% adoption</div>
                        </div>
                      </div>
                    </div>

                    <div className="absolute bottom-1/4 left-8 bg-card border border-border rounded-xl p-4 shadow-xl transform rotate-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                          <span className="text-purple-400 text-lg">💬</span>
                        </div>
                        <div>
                          <div className="text-sm font-medium">Team Chat</div>
                          <div className="text-xs text-muted-foreground">23 channels</div>
                        </div>
                      </div>
                    </div>

                    <div className="absolute bottom-0 right-8 bg-card border border-border rounded-xl p-4 shadow-xl transform -rotate-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                          <span className="text-orange-400 text-lg">📹</span>
                        </div>
                        <div>
                          <div className="text-sm font-medium">Contact Center</div>
                          <div className="text-xs text-muted-foreground">5 queues configured</div>
                        </div>
                      </div>
                    </div>

                    {/* Connection lines */}
                    <svg className="absolute inset-0 w-full h-full" style={{ zIndex: -1 }}>
                      <defs>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.1" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M 80 60 Q 200 150 320 100 Q 200 200 100 280 Q 200 300 300 350"
                        stroke="url(#lineGradient)"
                        strokeWidth="2"
                        fill="none"
                        strokeDasharray="8 4"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </main>
  );
}
